export interface LeavePolicy {
  minNoticeDays: number;
  maxDurationDays: number;
  seasonMonths: number;
  maxLeaveDaysPerSeason: number;
}

export interface LeaveSeasonUsage {
  start: number;
  end: number;
  usedDays: number;
  remainingDays: number;
  usedRequests: number;
  remainingRequests: number;
}

export const DEFAULT_LEAVE_POLICY: LeavePolicy = {
  minNoticeDays: 14,
  maxDurationDays: 14,
  seasonMonths: 3,
  maxLeaveDaysPerSeason: 2,
};

export function normalizeLeavePolicy(row?: Partial<LeavePolicy> | null): LeavePolicy {
  return {
    minNoticeDays: clamp(row?.minNoticeDays ?? DEFAULT_LEAVE_POLICY.minNoticeDays, 0, 90),
    maxDurationDays: clamp(row?.maxDurationDays ?? DEFAULT_LEAVE_POLICY.maxDurationDays, 1, 180),
    seasonMonths: clamp(row?.seasonMonths ?? DEFAULT_LEAVE_POLICY.seasonMonths, 1, 12),
    maxLeaveDaysPerSeason: normalizeSeasonRequestQuota(row?.maxLeaveDaysPerSeason),
  };
}

/** Stored field used to mean days; values above 12 are treated as the old default (14 days). */
export function normalizeSeasonRequestQuota(value?: number | null) {
  const n = Number.isFinite(value) ? Math.round(value as number) : DEFAULT_LEAVE_POLICY.maxLeaveDaysPerSeason;
  if (n > 12) return DEFAULT_LEAVE_POLICY.maxLeaveDaysPerSeason;
  return clamp(n, 1, 12);
}

export function startOfLocalDay(unix: number) {
  const d = new Date(unix * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

export function inclusiveDays(startUnix: number, endUnix: number) {
  const start = startOfLocalDay(startUnix);
  const end = startOfLocalDay(endUnix);
  if (end < start) return 0;
  return Math.floor((end - start) / 86400) + 1;
}

export function isoDateOffset(days: number, from = new Date()) {
  const d = new Date(from);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function seasonBounds(atUnix: number, seasonMonths = DEFAULT_LEAVE_POLICY.seasonMonths) {
  const months = clamp(seasonMonths, 1, 12);
  const persian = persianYmd(atUnix);
  if (persian) {
    const startMonth = Math.floor((persian.month - 1) / months) * months + 1;
    const start = unixForPersian(persian.year, startMonth, 1);
    let endYear = persian.year;
    let endMonth = startMonth + months;
    if (endMonth > 12) {
      endMonth -= 12;
      endYear += 1;
    }
    const nextStart = unixForPersian(endYear, endMonth, 1);
    return { start, end: nextStart - 1 };
  }
  const d = new Date(atUnix * 1000);
  const startMonth = Math.floor(d.getMonth() / months) * months;
  const start = new Date(d.getFullYear(), startMonth, 1);
  const next = new Date(d.getFullYear(), startMonth + months, 1);
  return {
    start: Math.floor(start.getTime() / 1000),
    end: Math.floor(next.getTime() / 1000) - 1,
  };
}

export function countLeaveDaysInSeason(
  rows: Array<{ leaveStart: number; leaveEnd: number; status: string }>,
  seasonStart: number,
  seasonEnd: number,
) {
  const days = new Set<number>();
  const from = startOfLocalDay(seasonStart);
  const to = startOfLocalDay(seasonEnd);
  for (const row of rows) {
    if (row.status === 'denied') continue;
    let t = Math.max(startOfLocalDay(row.leaveStart), from);
    const last = Math.min(startOfLocalDay(row.leaveEnd), to);
    for (; t <= last; t += 86400) days.add(t);
  }
  return days.size;
}

export function countLeaveRequestsInSeason(
  rows: Array<{ leaveStart: number; leaveEnd: number; status: string }>,
  seasonStart: number,
  seasonEnd: number,
) {
  return rows.filter((row) => {
    if (row.status === 'denied') return false;
    return row.leaveStart <= seasonEnd && row.leaveEnd >= seasonStart;
  }).length;
}

function clamp(value: number, min: number, max: number) {
  const n = Number.isFinite(value) ? Math.round(value) : min;
  return Math.min(max, Math.max(min, n));
}

function persianYmd(unix: number) {
  try {
    const parts = new Intl.DateTimeFormat('en-US-u-ca-persian', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(unix * 1000));
    const num = (type: Intl.DateTimeFormatPartTypes) =>
      parseInt((parts.find((p) => p.type === type)?.value ?? '').replace(/\D/g, ''), 10);
    const year = num('year');
    const month = num('month');
    const day = num('day');
    if (!year || !month || !day) return null;
    return { year, month, day };
  } catch {
    return null;
  }
}

function unixForPersian(year: number, month: number, day: number) {
  let lo = Math.floor(Date.UTC(year + 620, 0, 1) / 1000);
  let hi = Math.floor(Date.UTC(year + 623, 0, 1) / 1000);
  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const p = persianYmd(mid);
    if (!p) return startOfLocalDay(mid);
    const cmp = p.year !== year ? p.year - year : p.month !== month ? p.month - month : p.day - day;
    if (cmp < 0) lo = mid + 1;
    else hi = mid;
  }
  return startOfLocalDay(lo);
}
